"use client";

import { Divide, FileText, User } from "lucide-react";

import { badgeVariants } from "@krea/shared/design-system/Badge";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { cn } from "@krea/shared/utils";

type SourceType = "application" | "annualReport" | "kreaEstimated";

interface SourcePinProps {
  source: SourceType;
  className?: string;
}

const ANNUAL_REPORT_DESCRIPTION =
  "Figures extracted from the company's latest available annual report.";

const KREA_ESTIMATED_LABEL = "Krea estimated";
const KREA_ESTIMATED_DESCRIPTION =
  "Estimated by Krea based on information provided in the application, including accounting and bank transaction data.";

const SOURCE_CONFIG: Record<
  SourceType,
  {
    label: string;
    icon: React.ReactNode;
    description: string;
  }
> = {
  application: {
    label: "Customer reported",
    icon: <User size={12} />,
    description:
      "Self-reported by the applicant when submitting the application. Not independently verified.",
  },
  annualReport: {
    label: "Annual report",
    icon: <FileText size={12} />,
    description: "",
  },
  kreaEstimated: {
    label: KREA_ESTIMATED_LABEL,
    icon: <Divide size={12} />,
    description: KREA_ESTIMATED_DESCRIPTION,
  },
};

export function SourcePin({ source, className }: SourcePinProps) {
  const config = SOURCE_CONFIG[source];
  const description =
    source === "annualReport" ? ANNUAL_REPORT_DESCRIPTION : config.description;

  return (
    <ResponsiveTooltip
      trigger={
        <button
          type="button"
          className={cn(
            badgeVariants({ variant: "secondary" }),
            "rounded-xl cursor-help gap-1.5 py-1",
            "border-0 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary/40 focus-visible:ring-offset-1",
            className,
          )}
        >
          <span className="flex items-center justify-center">
            {config.icon}
          </span>
          <span>{config.label}</span>
        </button>
      }
      side="top"
      align="start"
      className="max-w-64"
    >
      <p className="text-sm font-semibold text-black mb-0.5">{config.label}</p>
      <p className="text-helper">{description}</p>
    </ResponsiveTooltip>
  );
}
